/**
 * Centralised permission constants for route guards.
 *
 * Each constant is the list of permissions that grants access to a
 * group of routes. `PermissionGate` passes if the user has ANY of
 * them.
 *
 * Convention: the **explicit** permission (`settings:manage`,
 * `orgs:manage`, `network:manage`) is what the role grants, and
 * `admin:*` is the platform-level escape hatch. Only superadmin holds
 * `*` (which the wildcard match resolves); network_admin holds
 * `network:manage` and nothing `admin:`-prefixed (CLA-920). Note that
 * clinic_admin / org_admin DO hold named `admin:settings` etc — that is
 * why the store's `isAdmin` is not a platform check; `isPlatformAdmin`
 * is (CLA-1249).
 *
 * Why constants?
 * - The route definitions used 46 inline arrays — easy to typo, easy
 *   to forget one when extending the vocabulary. One change here
 *   updates every route.
 * - Stage 2 of the RBAC cleanup adds a CI guard that iterates these
 *   constants and verifies at least one role in `role_permissions`
 *   grants each, so a future typo is a build failure not a runtime
 *   "Not authorised" page.
 *
 * See also:
 * - `auth.ts` `hasPermission()` for wildcard semantics
 * - `components/shared/PermissionGate.tsx` for the runtime check
 * - `claro-elixir/priv/repo/migrations/...grant_manage_perms...`
 *   for the SQL grants on roles
 */

/** Routes that manage a single clinic — `/manage/clinic/*`. */
export const MANAGE_CLINIC = ['settings:manage', 'admin:*'] as const

/** Routes that manage an org and its clinics — `/manage/org/*`. */
export const MANAGE_ORG = ['orgs:manage', 'admin:*'] as const

/** Routes that manage a network — `/manage/network/*`. */
export const MANAGE_NETWORK = ['network:manage', 'admin:*'] as const

/**
 * The commercial reports page — `/reports` (CLA-1307). Mirrors the
 * registry's own filter: money reports need `sales:read`, schedule reports
 * `settings:manage`. Either is enough to be offered at least one report;
 * the server decides which.
 */
export const REPORTS = ['sales:read', 'settings:manage', 'admin:*'] as const

/**
 * Platform-only routes — `/manage/platform/*`. Superadmin only. The one
 * exception is the Help library, which support may also open: its route is
 * gated on `PlatformStaffGate` (the store's `isPlatformStaff`), matching the
 * server's `platform_staff?` check on `/admin/help/articles`.
 */
export const MANAGE_PLATFORM = 'admin:*' as const

/**
 * Changing the diary — book, edit, move, cancel, mark Arrived / no-show,
 * start a consultation from an appointment, link a note, rosters and
 * absences (CLA-1577). The server refuses every one of those without it, so
 * the calendar offers none of them to a role that lacks it (clinic_viewer,
 * network_viewer). Reads need no permission beyond clinic membership.
 */
export const CALENDAR_WRITE = 'calendar:write' as const
